import React from "react";
import {
  SwapOutlined,
  ThunderboltOutlined,
  DesktopOutlined,
  AppstoreOutlined,
  SnippetsOutlined,
  DatabaseOutlined,
  DiffOutlined,
  ClusterOutlined,
  EyeOutlined,
  FileZipOutlined,
  ToolOutlined,
  DragOutlined,
  RobotOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  MinusCircleFilled,
  LoadingOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { resolveSubMeta } from "../../helper/masterSuiteFlow";

// Icon-name (from SUBCATEGORY_META) → Ant Design icon element.
const ICON_REGISTRY = {
  snippets: <SnippetsOutlined />,
  database: <DatabaseOutlined />,
  diff: <DiffOutlined />,
  cluster: <ClusterOutlined />,
  eye: <EyeOutlined />,
  filezip: <FileZipOutlined />,
  tool: <ToolOutlined />,
  drag: <DragOutlined />,
  robot: <RobotOutlined />,
  swap: <SwapOutlined />,
  thunderbolt: <ThunderboltOutlined />,
  desktop: <DesktopOutlined />,
  appstore: <AppstoreOutlined />,
};

// Small status glyph shown top-right in result mode.
const STATUS_ICON = {
  pass: <CheckCircleFilled className="ms-canvas-status ms-canvas-status--pass" />,
  fail: <CloseCircleFilled className="ms-canvas-status ms-canvas-status--fail" />,
  // Fan-out: some branches passed, others failed — amber, not red.
  partial: <ExclamationCircleFilled className="ms-canvas-status ms-canvas-status--partial" />,
  running: <LoadingOutlined className="ms-canvas-status ms-canvas-status--running" />,
  stopped: <MinusCircleFilled className="ms-canvas-status ms-canvas-status--stopped" />,
  skipped: <MinusCircleFilled className="ms-canvas-status ms-canvas-status--skipped" />,
};

// One workflow step as a card. Purely presentational: absolute position, drag
// wiring and selection are supplied by the parent canvas. Colour identity comes
// from the subcategory; run status (result mode) drives the ring + glyph.
function WorkflowNode({
  node,
  mode = "view",
  selected = false,
  isDragging = false,
  // "ok" | "bad" | null — set only while a re-parent drag is in flight.
  dropState = null,
  onClick,
  onMouseEnter,
  style,
  innerRef,
  dragHandleProps = {},
}) {
  const meta = resolveSubMeta(node.category, node.subCategory);
  const icon = ICON_REGISTRY[meta.icon] || <AppstoreOutlined />;
  const status = node.status || "";
  const isResult = mode === "result";
  const unreached = isResult && (status === "skipped" || status === "");

  const cls = [
    "ms-canvas-node",
    `ms-canvas-node--${mode}`,
    isResult && status ? `ms-canvas-node--st-${status}` : "",
    selected ? "ms-canvas-node--selected" : "",
    isDragging ? "ms-canvas-node--dragging" : "",
    dropState ? `ms-canvas-node--drop-${dropState}` : "",
    unreached ? "ms-canvas-node--unreached" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={innerRef}
      className={cls}
      style={{ "--ms-node-color": meta.color, ...style }}
      onClick={() => onClick?.(node)}
      onMouseEnter={onMouseEnter}
      title={node.title}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(node);
        }
      }}
      {...dragHandleProps}
    >
      {node.seq != null && <span className="ms-canvas-seq">{node.seq}</span>}
      {isResult && status ? STATUS_ICON[status] || null : null}

      <span className="ms-canvas-chip">{icon}</span>
      <span className="ms-canvas-body">
        <span className="ms-canvas-title">{node.title}</span>
        {node.subtitle ? (
          <span className="ms-canvas-target">{node.subtitle}</span>
        ) : (
          <span className="ms-canvas-target ms-canvas-target--muted">
            {meta.label}
          </span>
        )}
      </span>

      {/* Stop-if-failed. Shown only when ON: it defaults off, so badging every step
          would be noise on the common case and say nothing. This replaces the dashed
          cable, which encoded the inverse and so dashed a whole suite by default.
          Bottom-right is the one free corner — seq sits top-left, run status top-right. */}
      {node.stopIfFailed && (
        <span
          className="ms-canvas-halt"
          title="Stops this branch if this step fails"
        >
          <StopOutlined />
        </span>
      )}
    </div>
  );
}

export default WorkflowNode;
